/* A station's weekly grid: slots that each run a programme, in the station's time zone. Outside them the base
   format plays, so there are no "closed" hours. Same time rules as a zone's opening hours. */
import { For, Index, Show, createEffect, createSignal } from 'solid-js';
import { api } from '@app/api';
import type { GridSlot, Station } from './types';
import { ErrorLine, useAction } from './ui';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const PALETTE = ['#2a9d8f', '#e76f51', '#8a5cf6', '#e9c46a', '#3a86ff', '#d6336c', '#52b788', '#f4a261', '#6c757d', '#118ab2'];
const hhmm = (t: string) => t.slice(0, 5);
const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

/** Colour per programme, stable in the station's programme order (the base format is index 0 and gets none). */
export const colourOf = (s: Station, slug: string) => PALETTE[(s.programmes.findIndex((p) => p.slug === slug) - 1 + PALETTE.length) % PALETTE.length];

/** The week at a glance: one bar per day, slots placed by time; an overnight slot continues on the next day. */
function Week(props: { station: Station; slots: GridSlot[] }) {
  const pieces = (day: number) => props.slots.flatMap((s) => {
    const a = mins(s.start), b = mins(s.end), over = b <= a;
    const out: { from: number; to: number; slug: string }[] = [];
    if (s.weekday === day) out.push({ from: a, to: over ? 1440 : b, slug: s.programme });
    if (over && (s.weekday % 7) + 1 === day && b > 0) out.push({ from: 0, to: b, slug: s.programme });
    return out;
  });
  const name = (slug: string) => props.station.programmes.find((p) => p.slug === slug)?.name ?? slug;
  const base = () => props.station.programmes.find((p) => p.slug === 'default')?.name ?? 'Base format';
  return (
    <div class="flex flex-col gap-1" role="img" aria-label="The weekly grid at a glance">
      <For each={DAYS}>
        {(day, di) => (
          <div class="flex items-center gap-2">
            <span class="w-10 text-xs font-semibold text-ink-3">{day.slice(0, 3)}</span>
            <div class="relative h-6 flex-1 overflow-hidden rounded bg-static" title={`${base()} outside the slots`}>
              <For each={pieces(di() + 1)}>
                {(p) => (
                  <div class="absolute inset-y-0 truncate px-1 text-[11px] font-semibold leading-6 text-white"
                    style={{ left: `${(p.from / 1440) * 100}%`, width: `${((p.to - p.from) / 1440) * 100}%`, background: colourOf(props.station, p.slug) }}
                    title={name(p.slug)}>{name(p.slug)}</div>
                )}
              </For>
            </div>
          </div>
        )}
      </For>
      <div class="ml-12 flex justify-between text-[10px] text-ink-3 mono"><span>00</span><span>06</span><span>12</span><span>18</span><span>24</span></div>
    </div>
  );
}

/** `base`: the API the station lives under, /radio (operators) or /studio (its owner). */
export default function GridEditor(props: { station: Station; onSaved: (s: Station) => void; base?: string }) {
  const [slots, setSlots] = createSignal<GridSlot[]>([]);
  const a = useAction();
  const saved = () => props.station.grid.map((s) => ({ ...s, start: hhmm(s.start), end: hhmm(s.end) }));
  createEffect(() => setSlots(saved()));

  const programmes = () => props.station.programmes.filter((p) => p.slug !== 'default');
  const dirty = () => JSON.stringify(slots()) !== JSON.stringify(saved());
  const onDay = (d: number) => slots().map((s, i) => ({ s, i })).filter((x) => x.s.weekday === d);
  const set = (i: number, patch: Partial<GridSlot>) => setSlots(slots().map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => setSlots(slots().filter((_, k) => k !== i));
  const add = (d: number) => {
    const mine = onDay(d);
    const last = mine[mine.length - 1]?.s;
    const start = last ? last.end : '20:00';
    setSlots([...slots(), { weekday: d, start, end: start < '22:00' ? `${String(Number(start.slice(0, 2)) + 2).padStart(2, '0')}:00` : '00:00',
      programme: programmes()[0]?.slug ?? '' }]);
  };
  const save = () => a.run(async () => {
    props.onSaved(await api<Station>(`${props.base ?? '/radio'}/stations/${props.station.id}/grid`, { method: 'PUT', json: { slots: slots() } }));
  });

  return (
    <div class="flex flex-col gap-4">
      <Week station={props.station} slots={slots()} />
      <p class="text-sm text-ink-3">Times are in {props.station.timezone}. A slot that ends at or before its start runs past midnight.
        Outside the slots the base format plays.</p>
      <Show when={programmes().length} fallback={<p class="text-sm text-ink-3">Add a programme first; the grid places programmes.</p>}>
        <table class="table table-sm">
          <tbody>
            <For each={DAYS}>
              {(day, di) => (
                <tr class="align-top">
                  <th scope="row" class="w-28 pt-3 font-semibold">{day}</th>
                  <td class="flex flex-col gap-2">
                    <Index each={onDay(di() + 1)} fallback={<span class="pt-1.5 text-sm text-ink-3">Base format all day</span>}>
                      {(x) => (
                        <div class="flex flex-wrap items-center gap-2">
                          <span class="h-3 w-3 rounded-full" style={{ background: colourOf(props.station, x().s.programme) }} aria-hidden="true" />
                          <input type="time" class="input input-sm input-bordered mono w-36" value={x().s.start}
                            aria-label={`${day} starts`} onChange={(e) => set(x().i, { start: e.currentTarget.value })} />
                          <span class="text-ink-3">to</span>
                          <input type="time" class="input input-sm input-bordered mono w-36" value={x().s.end}
                            aria-label={`${day} ends`} onChange={(e) => set(x().i, { end: e.currentTarget.value })} />
                          <Show when={x().s.end <= x().s.start}><span class="text-xs text-ink-3">next day</span></Show>
                          <select class="select select-sm select-bordered" value={x().s.programme}
                            aria-label={`${day} ${x().s.start} programme`} onChange={(e) => set(x().i, { programme: e.currentTarget.value })}>
                            <For each={programmes()}>{(p) => <option value={p.slug}>{p.name}</option>}</For>
                          </select>
                          <button class="btn btn-ghost btn-xs" aria-label={`Remove ${day} ${x().s.start}`} onClick={() => remove(x().i)}>✕</button>
                        </div>
                      )}
                    </Index>
                  </td>
                  <td class="w-24 text-right"><button class="btn btn-ghost btn-xs" onClick={() => add(di() + 1)}>+ Slot</button></td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>
      <Show when={dirty()}>
        <div class="flex gap-2">
          <button class="btn btn-sm btn-primary" disabled={a.busy()} onClick={save}>{a.busy() ? 'Saving…' : 'Save grid'}</button>
          <button class="btn btn-ghost btn-sm" onClick={() => setSlots(saved())}>Undo changes</button>
        </div>
      </Show>
      <ErrorLine error={a.error} />
    </div>
  );
}
